import {
  getFormConfiguration,
  validateField,
  validateGridCell,
  validateForm,
  validateGrid,
  shouldAutoSave,
  updateFieldError as addFieldError,
  updateGridFieldError as addGridFieldError,
  updateGridFieldVis,
  updateFieldVis,
  clearTableRowError,
  FormDataWrapper
} from "../../form/index.js";
import { SAVE_INFO_STATUS } from "../../constants/index.js";
import { PRODUCTS } from "../../constants/account.constants.js";
import { DataService, PublicService } from "../services/index.js";
import { isEmpty, cloneInstanceObject } from "../utils/utils.js";
import { createStoreWithImmer } from "./base.js";
import { useSectionStore } from "./section.store.js";

const STORE = {
  formData: null,
  flowData: {},
  schema: {},
  modelConfig: {},
  error: {},
  visiblity: {},
  shownOnScreen: {},
  pendingSignature: [],
  geoFieldsList: {},
  systemFields: [],
  gridRowValidationError: {},
  currentUser: null,
  isSplitView: false,
  listApiData: {}, //its added because the checkbox,radio listOption need in print form.
  sections: [],
  activeSection: {},
  isTableExpanded: false,
  childTableViewMode: {},
  saveTriggered: null,
  uploadPendingFields: {},
  idp: [],
  tableSelectedRows: {},
  saveInfoStatus: SAVE_INFO_STATUS.DEFAULT
};

export function createFormStore(uId) {
  const useFormStore = createStoreWithImmer(uId, stateFunction);
  return useFormStore;
}

const stateFunction = (setState, getState) => ({
  ...STORE,
  init: (formData, schema, flowData) => {
    let systemFields = getState().systemFields;
    let modelConfig = getFormConfiguration(schema, flowData, systemFields);
    setState(() => {
      return {
        formData: formData, //why is it need to be updated here
        schema: schema,
        flowData: flowData,
        modelConfig: modelConfig
      };
    });
  },
  updateModelConfig: (modelId, columnId, updatedColumnData = {}) => {
    setState((state) => {
      if (!state.modelConfig[modelId]) {
        state.modelConfig[modelId] = {};
      }
      if (!state.modelConfig[modelId][columnId]) {
        state.modelConfig[modelId][columnId] = { ...updatedColumnData };
      } else if (updatedColumnData) {
        state.modelConfig[modelId][columnId] = {
          ...state.modelConfig[modelId][columnId],
          ...updatedColumnData
        };
      }
    });
  },
  updateField: (value, field, saveChanges) => {
    let formData = getState().formData;
    formData && formData.update(field.Id, value);
    // setState(() => {
    //   return {
    //     formData: FormDataWrapper(formData.dataSet, formData.changeSet)
    //   };
    // });
    getState().validateFormField(field, formData);
    if (saveChanges && shouldAutoSave(field, saveChanges)) {
      return getState().saveChanges(formData);
    }
  },

  addTableRow: ({
    tableId,
    count = 1,
    index = -1,
    data = {},
    autoSave = true
  }) => {
    let formData = getState().formData;
    for (let i = 0; i < count; i++) {
      tableId && formData.create(tableId, index, data);
    }
    if (autoSave) {
      return getState().saveChanges(formData);
    }
    return Promise.resolve(formData);
  },
  /**
   * Add rows to a specified table in the form data.
   *
   * @param {object} params - Object containing parameters.
   * @param {string} params.tableId - The ID of the table to which rows will be added.
   * @param {number} [params.index=-1] - The index after which to insert the new rows. Default is -1, indicating to append the rows to the end of the table.
   * @param {Array} [params.data=[]] - List of rows to be added to the table. Each row is an object representing the data for a single row.
   * @returns {Promise} A Promise that resolves with the updated form data after adding the rows.
   *
   * @description
   * - If index is -1, the new rows will be appended to the end of the table.
   * - The index specified by index determines the position after which the new rows will be inserted.
   * - The function retrieves the current form data state, adds the rows to the specified table using the createMulti method, and then saves the changes to the form data.
   */

  addTableRows: ({ tableId, index = -1, data = [] }) => {
    if (tableId) {
      let formData = getState().formData;
      formData.createMulti(tableId, index, data);
      return getState().saveChanges(formData);
    }
  },

  deleteTableRow: (tableId, rowIds) => {
    let formData = getState().formData;
    rowIds.forEach((id) => {
      formData.delete(tableId, id);
    });
    getState().onGridRowDelete(tableId, rowIds);
    return getState().saveChanges(formData);
  },
  onGridRowDelete: (tableId, rowIds) => {
    let errorObj = getState().error;
    errorObj = clearTableRowError(tableId, rowIds, errorObj);
    setState(() => {
      return { error: errorObj };
    });
  },
  updateTableField: (
    tableId,
    rowId,
    field,
    value,
    saveChanges,
    updateSaveStatus
  ) => {
    let formData = getState().formData;
    let visiblity = getState().visiblity;
    let tableRows = formData.getList(tableId);
    let tableRow = tableRows.find((row) => row.getValue("_id") === rowId);
    if (tableRow) {
      tableRow.update(field.id || field.Id, value);
    }
    setState(() => {
      return {
        formData: FormDataWrapper(formData.dataSet, formData.changeSet)
      };
    });

    let errorObj = validateGridCell(
      field.modelConfig,
      tableRow,
      tableId,
      visiblity,
      formData,
      tableRows
    );
    errorObj &&
      getState().updateGridFieldError(
        errorObj.modelId,
        errorObj.rowId,
        errorObj.fieldId,
        errorObj.error
      );
    if (saveChanges && shouldAutoSave(field, saveChanges)) {
      return getState().saveChanges(formData, updateSaveStatus);
    }
  },
  saveChanges: (formData, updateSaveStatus = false) => {
    let changeSummary = formData?.getChangeSummary();
    let flowData = { ...getState().flowData };
    flowData.currentUser = getState().currentUser;
    return new Promise((resolve) => {
      if (flowData.isAdmin) {
        let [promise] = saveData(changeSummary, flowData);
        promise().then((changeSummary) => {
          getState().mergeChanges(formData, changeSummary);
          resolve();
          if (updateSaveStatus) {
            getState().setSaveCompleted(true);
            setTimeout(() => {
              return getState().setSaveCompleted(false);
            }, 1000);
          }
        });
      } else {
        saveData(changeSummary, flowData, (err, data, onDone) => {
          getState().mergeChanges(formData, data);
          let updatedChangeSummary = getState().getChangeSummary();
          updatedChangeSummary &&
            Reflect.deleteProperty(updatedChangeSummary, "Table::");
          onDone(updatedChangeSummary);
          resolve(data);
          if (updateSaveStatus) {
            getState().setSaveCompleted(true);
            setTimeout(() => {
              return getState().setSaveCompleted(false);
            }, 1000);
          }
        });
      }
    });
  },
  saveTableChanges: (formData, changeSummary, tableId) => {
    formData = getState().formData;
    let flowData = { ...getState().flowData };
    flowData.currentUser = getState().currentUser;
    return new Promise((resolve) => {
      if (formData) {
        formData.merge(changeSummary);
        setState(() => {
          let formDataUpdated = FormDataWrapper(
            formData.dataSet,
            formData.changeSet
          );
          return { formData: formDataUpdated };
        });
      }
      if (flowData.isAdmin) {
        let [promise] = saveData(changeSummary, flowData);
        promise().then((changeSummary) => {
          getState().mergeChanges(formData, changeSummary);
          resolve();
        });
      } else {
        saveData(changeSummary, flowData, (err, data, onDone) => {
          getState().mergeChanges(formData, data);
          // let updatedChangeSummary = getState().getChangeSummary();
          // updatedChangeSummary &&
          //   Reflect.deleteProperty(updatedChangeSummary, "Table::");
          getState().validateGridCells(
            formData,
            tableId,
            changeSummary[`Table::${tableId}`]
          );
          onDone();
          resolve(data);
        });
      }
    });
  },
  // This below function to validate the grid on bulk operations
  validateGridCells: (formData, tableId, changeSummary) => {
    let modelConfig = getState().modelConfig;
    let visiblity = getState().visiblity;
    let formErrors = getState().error;
    let error = validateGrid(
      formData,
      tableId,
      modelConfig,
      formErrors,
      visiblity,
      changeSummary
    );
    setState(() => {
      return { error: error };
    });
  },
  mergeChanges: (formData, changeSummary) => {
    // if (!formData) {
    //   formData = getState().formData;
    // }
    formData = getState().formData;
    if (!changeSummary) {
      changeSummary = formData?.getChangeSummary();
    }
    if (formData) {
      formData.merge(changeSummary);
      // Todo : change merge logic - don't create new Model
      setState(() => {
        let formDataUpdated = FormDataWrapper(
          formData.dataSet,
          formData.changeSet
        );
        return { formData: formDataUpdated };
      });
    }
  },
  getChangeSummary: () => {
    let formData = getState().formData;
    return formData?.getChangeSummary();
  },
  validateFormField: (field, formData) => {
    let schema = getState().schema;
    if (isEmpty(schema)) return;
    let modelConfig = getState().modelConfig;
    let modelId = schema.getModel().getValue("Id");
    let visiblity = getState().visiblity;
    let parentModelCofiguration = modelConfig[modelId];
    let column = schema.getObject(field.Column);
    if (column) {
      let errorObj = validateField(
        column.getValue("Id"),
        formData,
        parentModelCofiguration,
        visiblity
      );
      errorObj && getState().updateFieldError(errorObj.fieldId, errorObj.error);
    }
  },
  updateGridFieldError: (tableId, rowId, fieldId, error) => {
    let errorObj = getState().error;
    errorObj = addGridFieldError(tableId, rowId, fieldId, error, errorObj);
    setState(() => {
      return { error: errorObj };
    });
  },
  updateFieldError: (fieldId, error) => {
    let errorObj = getState().error;
    errorObj = addFieldError(fieldId, error, errorObj);
    setState(() => {
      return { error: errorObj };
    });
  },
  onFieldRemoved: (fieldId, columnId, tableId, rowId) => {
    getState().updateFieldError(fieldId);
    // let flowId = getState().flowData.flowId;
    // flowId &&
    //   getState().updateModelConfig(flowId, columnId, {
    //     renderedOnUI: false
    //   });
    let visiblity = getState().visiblity;
    let isVisiblie = false;
    if (tableId && rowId) {
      visiblity = updateGridFieldVis(
        tableId,
        rowId,
        columnId,
        isVisiblie,
        visiblity
      );
    } else {
      visiblity = updateFieldVis(columnId, isVisiblie, visiblity);
    }
    setState(() => {
      return { visiblity: visiblity };
    });
  },
  onFieldHiddenOnScreen: (fieldId, columnId) => {
    let shownOnScreen = getState().shownOnScreen;
    let isVisiblie = false;
    shownOnScreen = updateFieldVis(columnId, isVisiblie, shownOnScreen);
    setState(() => {
      return { shownOnScreen: shownOnScreen };
    });
  },
  updateGeoList: (fieldId, value) => {
    if (fieldId && value) {
      let geoList = { ...getState().geoFieldsList };
      if (!geoList[fieldId]) {
        geoList[fieldId] = {};
      }
      geoList[fieldId] = value;
      setState(() => {
        return { geoFieldsList: geoList };
      });
    }
  },
  onFieldAdded: (fieldId, columnId, tableId, rowId) => {
    if (fieldId && columnId) {
      // let flowData = getState().flowData;
      // flowData.flowId &&
      //   getState().updateModelConfig(flowData.flowId, columnId, {
      //     renderedOnUI: true
      //   });
      let visiblity = getState().visiblity;
      let isVisiblie = true;
      if (tableId && rowId) {
        visiblity = updateGridFieldVis(
          tableId,
          rowId,
          columnId,
          isVisiblie,
          visiblity
        );
      } else {
        visiblity = updateFieldVis(columnId, isVisiblie, visiblity);
      }
      setState(() => {
        return { visiblity: visiblity };
      });
    }
  },
  onFieldShownOnScreen: (fieldId, columnId) => {
    let shownOnScreen = getState().shownOnScreen;
    let isVisiblie = true;
    shownOnScreen = updateFieldVis(columnId, isVisiblie, shownOnScreen);
    setState(() => {
      return { shownOnScreen: shownOnScreen };
    });
  },
  onTableAdded: (tableId, data, visibleOnScreen) => {
    if (tableId && data) {
      setState((state) => {
        if (!state.visiblity[tableId]) {
          state.visiblity[tableId] = {};
        }
        state.visiblity[tableId] = data;
        if (!state.shownOnScreen[tableId]) {
          state.shownOnScreen[tableId] = {};
        }
        state.shownOnScreen[tableId] = visibleOnScreen;
      });
    }
  },
  updatePendingValues: async (formData, geoValuesList) => {
    if (Object.keys(geoValuesList).length > 0) {
      for (let fieldId in geoValuesList) {
        formData && formData.update(fieldId, geoValuesList[fieldId]);
      }
      setState(() => {
        return { geoFieldsList: {} };
      });
      await getState().saveChanges(formData);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 500);
      });
    }
  },
  validateFullForm: async () => {
    let formData = getState().formData;
    let isValid = true;
    let geoValuesList = getState().geoFieldsList;
    let sections = getState().sections;
    await getState().updatePendingValues(formData, geoValuesList);
    await expandAllRows(formData, sections);
    let modelConfig = getState().modelConfig;
    let schema = getState().schema;
    let rootId = getState().flowData.flowId;
    let visiblity = getState().visiblity;
    let gridRowValidationError = getState().gridRowValidationError;
    let error = validateForm(formData, modelConfig, rootId, visiblity);
    // let error = getState().error;
    setState(() => {
      return { error: error };
    });

    for (let modelName in error) {
      if (modelName === "_root") {
        let fieldError = Object.keys(error["_root"]) || [];
        if (!fieldError.isEmpty()) {
          isValid = false;
        }
      } else {
        for (let row in error[modelName]) {
          // Here we ignoring the hidden table's errors
          let currentModel = schema.getObject(modelName);
          let column = schema.getObject(currentModel.getValue("Column"));
          let columnId = column.getValue("Id");
          let { isHidden } = modelConfig[rootId][columnId];
          if (!isHidden) {
            let fieldError = Object.keys(error[modelName][row]) || [];
            if (!fieldError.isEmpty()) {
              isValid = false;
            }
          }
        }
      }
    }

    if (Object.keys(gridRowValidationError).length > 0) {
      isValid = false;
    }
    return isValid;
  },
  setSignatureStatus: (fieldId, status) => {
    let pendingSignature = getState().pendingSignature;
    pendingSignature = [...pendingSignature];
    if (status === "Done") {
      pendingSignature.pop(fieldId);
    } else {
      pendingSignature.push(fieldId);
    }
    setState(() => {
      return { pendingSignature: pendingSignature };
    });
  },
  setGridRowValidationError: (modelId, error) => {
    let gridRowValidationError = getState().gridRowValidationError;
    gridRowValidationError = { ...gridRowValidationError };
    if (error) {
      gridRowValidationError[modelId] = error;
    } else {
      Reflect.deleteProperty(gridRowValidationError, modelId);
    }
    setState(() => {
      return { gridRowValidationError: gridRowValidationError };
    });
  },
  toJSON: () => {
    let formData = getState().formData;
    let newFormData = cloneInstanceObject(formData);
    const changes = formData.getChangeSummary();
    newFormData.merge(changes);
    return newFormData;
  },
  setListApiData: (data) => {
    let { key, listOption } = data;
    setState(() => {
      return {
        listApiData: {
          ...getState().listApiData,
          [key]: listOption
        }
      };
    });
  },
  addSection: (data) => {
    let sections = getState().sections;
    const sectionIndex = sections.findIndex(
      (section) => section.id === data.id
    );
    if (sectionIndex === -1) {
      setState(() => {
        return { sections: [...sections, data] };
      });
    }
  },
  updateSectionVisibility: (id, isHidden, accrodianGroup) => {
    let sections = getState().sections;
    sections = sections.map((data) => {
      let section = { ...data };
      if (section.id === id) {
        section.isHidden = isHidden;
        if (accrodianGroup) {
          section.accrodianGroup = accrodianGroup;
        }
      }
      return section;
    });
    setState(() => {
      return { sections: sections };
    });
  },
  setActiveSection: (section) => {
    setState(() => {
      return { activeSection: section };
    });
  },
  setSelectedRows: (tableId, selectedRows) => {
    setState((state) => {
      state.tableSelectedRows[tableId] = selectedRows;
    });
  },
  resetSectionStore: () => {
    setState(() => {
      return { activeSection: {}, sections: [] };
    });
  },
  reset: () => {
    setState(() => ({
      ...STORE
    }));
  },
  setSaveCompleted: (status) => {
    setState(() => ({
      saveTriggered: status
    }));
  },
  switchViewMode: (mode, modelId) => {
    setState((state) => {
      state.childTableViewMode[modelId] = mode;
    });
  },
  setSaveInfoProgress: (data = SAVE_INFO_STATUS.PROGRESS) => {
    setState((state) => {
      state.saveInfoStatus = data;
    });
  },
  setSaveInfoCompleted: (data = SAVE_INFO_STATUS.SUCCESS) => {
    setState((state) => {
      state.saveInfoStatus = data;
      setTimeout(() => {
        setState((state) => {
          state.saveInfoStatus = SAVE_INFO_STATUS.DEFAULT;
        });
      }, 2000);
    });
  }
});

function saveData(params, flowData, callback) {
  // const flowData = getState().flowData;
  Reflect.deleteProperty(params, "Table::");
  if (flowData.isPublicForm) {
    return PublicService.postq(
      flowData.saveUrl || flowData.baseUrl,
      {
        data: params,
        options: {
          recaptcha_action: "savePublicForm"
        },
        toast: flowData.flowType !== PRODUCTS.CASE
      },
      callback
    );
  } else if (flowData.isAdmin) {
    return DataService.update(flowData.saveUrl || flowData.baseUrl, {
      data: params,
      toast: flowData.flowType !== PRODUCTS.CASE
    });
  } else {
    let header = {};
    if (flowData.mode === "Preview" && flowData.currentUser) {
      header = {
        "X-Preview-Impersonated-User-Id": flowData.currentUser["_id"]
      };
    }
    return DataService.postq(
      flowData.saveUrl || flowData.baseUrl,
      {
        data: params,
        header: header,
        toast: flowData.flowType !== PRODUCTS.CASE
      },
      callback
    );
  }
}

function expandAllRows(formData, sections) {
  let pwaSections = useSectionStore.getState().sections;
  let newSections = !isEmpty(pwaSections) ? pwaSections : sections;

  let isPWA = false;
  newSections.forEach((section) => {
    if (section.accrodianGroup) {
      isPWA = true;
      let rows = formData.getList(section.accrodianGroup.id);
      rows = rows.map((row) => {
        return row.id;
      });
      if (section.accrodianGroup.accrodianGroupRef) {
        section.accrodianGroup.accrodianGroupRef.expandAll(rows);
      }
    }
  });

  return new Promise((resolve) => {
    isPWA
      ? setTimeout(() => {
          resolve();
        }, 400)
      : resolve();
  });
}
